/**
 * Going to try implementing some basic path computation algorithms in
 * Javascript to package with the app. No effort will be made to optimize these
 * algorithms.  I'll be mostly translating from some simple Python implementations.
 *
 * Created by Greg on 5/1/2016.
 */

// TEST CODE:
// import * as medExNet from '../netSamples/KShortTest2Net.json';
// import {netValidate} from './netValid';
// var testNet = netValidate(JSON.stringify(medExNet)).netObj;
// console.log("Path compute test");
// var result = basicDijkstra(testNet, "A4");
// console.log(result.distance);
// console.log(result.previous);
// console.log(getPaths("A4", result.previous, testNet));
// console.log(getPath("A4", "B9", result.previous, testNet));
// var result2 = widestDijkstra(testNet, "B9");
// console.log(result2.capacities);
// console.log(result.previous);
// console.log(getPaths("B9", result2.previous, testNet));
// console.log(getPath("B9", "C29", result2.previous, testNet));
// var tree1 = minSpanTree(testNet);
// console.log(tree1);

/*
    We'll work with string node IDs and such...
 */
function basicDijkstra(net, src, wt="weight") {
    let previous = new Map(); //   previous hops
    let T = new Set();
    let V = new Set(net.nodes.map(n=>n.id)); // Initially set of all nodes
    T.add(src);
    V.delete(src);
    let distance = new Map();  // distance between nodes
    distance.set(src, 0.0);
    // Initialize distances
    for (let v of V) {
        if (hasEdge(net, src, v)) {
            distance.set(v, linkParm(net, src, v, wt));
            previous.set(v, src);
        } else {
            distance.set(v, Infinity);
        }
    }
    while (V.size > 0) {
        // Find the node with the smallest distance in the set V
        let smallDist = Infinity;
        let u = null; // Smallest distance node
        for (let v of V) {
            if (distance.get(v) <= smallDist) {
                smallDist = distance.get(v);
                u = v;
            }
        }
        T.add(u);
        V.delete(u);
        // update distances
        for (let v of V) {
            if (hasEdge(net, u, v)) {
                if (distance.get(v) > distance.get(u) + linkParm(net, u, v, wt)) {
                    distance.set(v, distance.get(u) + linkParm(net, u, v, wt));
                    previous.set(v, u);
                }
            }
        }
    }
    return {distance: distance, previous: previous};
}


function widestDijkstra(net, src, bw="capacity") {
    let previous = new Map(); //   previous hops
    let T = new Set();
    let V = new Set(net.nodes.map(n=>n.id)); // Initially set of all nodes
    T.add(src);
    V.delete(src);
    let cap = new Map();  // capacities between nodes
    cap.set(src, Infinity);
    // Initialize capacity
    for (let v of V) {
        if (hasEdge(net, src, v)) {
            cap.set(v, linkParm(net, src, v, bw));
            previous.set(v, src);
        } else {
            cap.set(v, 0.0);
        }
    }
    while (V.size > 0) {
        // Find the node with the maximum capacity in the set V
        let maxCap = 0.0;
        let u = null; // Max cap node
        for (let v of V) {
            if (cap.get(v) >= maxCap) {
                maxCap = cap.get(v);
                u = v;
            }
        }
        T.add(u);
        V.delete(u);
        // update capacities
        for (let v of V) {
            if (hasEdge(net, u, v)) {
                if (cap.get(v) < Math.min(cap.get(u), linkParm(net, u, v, bw))) {
                    cap.set(v, Math.min(cap.get(u), linkParm(net, u, v, bw)));
                    previous.set(v, u);
                }
            }
        }
    }
    return {capacities: cap, previous: previous};
}

/*
    Based on Prim's algorithm as described on page 41 of Gibbons "Algorithmic
    Graph Theory".
    Or just as good see https://en.wikipedia.org/wiki/Prim's_algorithm
 */
function minSpanTree(net, wt="weight") {
    let tree = [];
    let Vprime = new Set();
    let V = new Set(net.nodes.map(n=>n.id)); // Initially set of all nodes
    let u = net.nodes[0].id; // We can use any node to start
    V.delete(u);
    Vprime.add(u);
    // Initialize labels
    let label = new Map();
    let connLink = new Map(); // Used to keep track of which links connects this node to tree
    for (let v of V) {
        if (hasEdge(net, u, v)) {
            label.set(v, linkParm(net, u, v, wt));
            connLink.set(v, [u, v]);
        } else {
            label.set(v, Infinity);
            connLink.set(v, null);
        }
    }
    while (V.size > 0) {
        let smallDist = Infinity;
        u = null; // Smallest label node
        for (let v of V) {
            if (label.get(v) < smallDist) {
                smallDist = label.get(v);
                u = v;
            }
        }
        tree.push(connLink.get(u));
        V.delete(u);
        Vprime.add(u);
        // Update labels and connecting links
        for (let v of V) {
            if (hasEdge(net, v, u)) {
                if (linkParm(net, v, u, wt) < label.get(v)) {
                    label.set(v, linkParm(net, v, u, wt));
                    connLink.set(v, [v, u]);
                }
            }
        }
    }
    return tree;
}

// Get's a nice list of path objects from a previous hop map.
function getPaths(s, previous, net, wt="weight", cap="capacity") {
    let pathList = [];
    for (let t of previous.keys()) {
        let pathObj = {};
        let nodeList = [];
        if (t !== s) {
            let v = t;
            let cost = 0.0;
            let capacity = Infinity;
            while (v !== s) {
                nodeList.push(v);
                let v_old = v;
                v = previous.get(v);
                cost += linkParm(net, v, v_old, wt);
                capacity = Math.min(capacity, linkParm(net, v, v_old, cap));
            }
            nodeList.push(s);
            nodeList.reverse();
            pathObj = {cost: cost, capacity: capacity, nodeList: nodeList};
            pathList.push(pathObj);
        }
    }
    return pathList;
}

// Get's a single path object between s and t based on a previous hop map.
function getPath(s, t, previous, net, wt="weight", cap="capacity") {
    let pathObj = {};
    let nodeList = [];
    if (t !== s) {
        let v = t;
        let cost = 0.0
        let capacity = Infinity;
        while (v !== s) {
            nodeList.push(v);
            let v_old = v;
            v = previous.get(v);
            cost += linkParm(net, v, v_old, wt);
            capacity = Math.min(capacity, linkParm(net, v, v_old, cap));
        }
        nodeList.push(s);
        nodeList.reverse();
        pathObj = {cost: cost, capacity: capacity, nodeList: nodeList};
    }
    return pathObj;
}


function hasEdge(net, s, v) {
    let links = net.links;
    if (links.find(l => l.source === s && l.target === v)) {
        return true;
    }
    if (links.find(l => l.source === v && l.target === s)) {
        return true;
    }
    return false;
}

/*
    Returns the value of the parameter "parm" for the link between nodes
    u and v.
 */
function linkParm(net, u, v, parm) {
    let links = net.links;
    let link = links.find(l => l.source === u && l.target === v);
    if (link) {
        return link[parm];
    }
    link = links.find(l => l.source === v && l.target === u);
    if (link) {
        return link[parm];
    }
    return null;
}



/* Need a check for connectedness. Will start with a depth first graph
    traversal. This is based on the non-recursive algorithm from:
    https://en.wikipedia.org/wiki/Depth-first_search

 */
function depthFirstConnected(net) {
    let nodes = net.nodes;
    if (nodes.length === 0) {
        return false;
    }
    let v = net.nodes[0].id;  // Choose a starting node
    let treeNodes = new Set();
    let nodeStack = [v];
    let adjMap = genAdjs(net);

    let unvisited = new Set(nodes.map(n => n.id));
    unvisited.delete(v);
    let descendents = [];
    while (nodeStack.length > 0) {
        v = nodeStack.pop();
        if (!treeNodes.has(v)) {
            treeNodes.add(v);
            descendents = [...adjMap.get(v)];
            for (let node of descendents) {
                nodeStack.push(node);
            }
        }
    }
    return treeNodes.size === nodes.length;
}

/*
    Create an adjacency map for the network. We'll map a node id to
    a set of adjacent nodes.
 */
function genAdjs(net) {
    let adjMap = new Map();
    // For each node add a set to hold adjaciencies
    for (let node of net.nodes) {
        adjMap.set(node.id, new Set());
    }
    for (let link of net.links) {
        adjMap.get(link.source).add(link.target);
        adjMap.get(link.target).add(link.source);
    }
    return adjMap;
}

export {basicDijkstra, widestDijkstra, minSpanTree, linkParm, depthFirstConnected, getPath, getPaths}


// WEBPACK FOOTER //
// ./src/pathComp/PathComputations.js